import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import React from "react";
import { ScrollView } from "react-native";
import { TPlan } from "../../../../utils/types";
import { SessionContext } from "../../../../contexts";
import { config } from "../../../../config";
import { logUIError } from "../../../../services/api-misc";

const stripePublishableKey = process.env.STRIPE_PUBLISHABLE_KEY ?? "";
const stripePromise = loadStripe(stripePublishableKey);
const protocol = config.coreApi.protocol;
const host = config.coreApi.host;
const urlPrefix = `${protocol}://${host}/clinics/v1`;

type TStripCheckoutFormProps = {
  plan: TPlan;
  apiKey?: string;
  clinicUuid?: string;
};

const StripCheckoutForm: React.FC<TStripCheckoutFormProps> = ({
  plan,
  apiKey,
  clinicUuid,
}) => {
  const session = React.useContext(SessionContext)?.session;
  const isApiKeyMode = Boolean(apiKey && clinicUuid);

  if (!isApiKeyMode && !session) return null;

  const fetchClientSecret = React.useCallback(async () => {
    try {
      const targetClinicId = isApiKeyMode ? clinicUuid : session?.clinicId;

      if (!targetClinicId) {
        throw new Error("Clinic identifier is required to start checkout.");
      }

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (isApiKeyMode) {
        headers["X-Clinic-Api-Key"] = apiKey as string;
      } else {
        headers["Authorization"] = `Bearer ${session?.accessToken}`;
      }

      const response = await fetch(
        `${urlPrefix}/billing/${targetClinicId}/create-stripe-checkout-session`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ plan }),
        },
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(
            "API key is missing the billing:manage scope required for checkout.",
          );
        }
        const text = await response.text();
        throw new Error(
          `HTTP error ${response.status}: ${text || "Failed to create checkout session"}`,
        );
      }

      const data = await response.json();

      if (!data.client_secret) {
        throw new Error("Client secret not found in the response");
      }

      return data.client_secret;
    } catch (error) {
      console.error("Error fetching client secret:", error);
      logUIError({
        message: "Failed to fetch Stripe checkout client secret",
        page_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        user_email: session?.userEmail || undefined,
        clinic_name: session?.clinicName || undefined,
        clinic_id: session?.clinicId || clinicUuid || undefined,
        context: {
          component: "StripeCheckoutForm",
          plan,
          isApiKeyMode,
          targetClinicId: isApiKeyMode ? clinicUuid : session?.clinicId,
          error: error instanceof Error ? error.message : String(error),
        },
      }).catch((logErr) =>
        console.warn("logUIError failed in StripeCheckoutForm", logErr),
      );
      throw error;
    }
  }, [apiKey, clinicUuid, isApiKeyMode, plan, session]);

  const options = { fetchClientSecret };

  return (
    <ScrollView
      showsHorizontalScrollIndicator={false}
      showsVerticalScrollIndicator={false}
    >
      <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </ScrollView>
  );
};

export default StripCheckoutForm;
